import { datadogLogs } from "@datadog/browser-logs";
import { DEFAULT_CURRENCY, moneyFormat } from "@redotech/money/util";
import {
  DraftReturn,
  PickupData,
} from "@redotech/redo-model/draft-return/draft-return";
import { Channel, PickupLocation } from "@redotech/redo-model/return";
import { useCallback, useContext, useMemo, useState } from "react";
import { ReturnContext } from "../../contexts/return";
import { RpcClientContext } from "../../contexts/rpc";
import { Navigator, RouteName, StackParamList } from "../../navigator";
import { CustomerPortalAppSettings } from "../../settings";
import { getValidDate } from "../../shipment-method/util";
import { PACKAGE_PICKUP_UPSELL_METRICS_LOG_STAMP } from "../../util";
import { isValidNonDefaultPhoneNumber } from "../../utils/phone-validator";
import { useDatePickerState } from "./use-date-picker-state";
import { useLocationPickerState } from "./use-location-picker-state";
import { usePickupSubmission } from "./use-pickup-submission";

const CANCEL_PICKUP_CLICKED_LOG_STAMP = "cancelPickupClicked";

export const MIN_DAYS_FOR_MANUAL_REVIEW = 7;
export const MIN_DAYS = 1;
export const MAX_DAYS = 14;

export interface UsePackagePickupResponse {
  datePickerState: ReturnType<typeof useDatePickerState>;
  draftReturn?: DraftReturn;
  handleDateChange: (date?: Date) => void;
  updatePickupInstructions: (text: string) => void;
  updatePhone: (phone: string) => void;
  validatePhone: (phone: string) => string[];
  isUpdatingMethod: boolean;
  locationPickerState: ReturnType<typeof useLocationPickerState>;
  texts: {
    addPickupButton: string;
    cancelButton: string;
    description: string;
    header: string;
    pickupDateLabel: string;
    pickupLocationLabel: string;
    pickupLocationTip: string;
    selectLocationButton: string;
    specialInstructionsLabel: string;
    textReminderDescription: string;
    textReminderLabel: string;
    textReminderNotification: string;
    phoneLabel: string;
  };
  selectedPickupLocation: string | undefined;
  shippingDetails: { draftReturnId?: string; pickupData: PickupData };
  submitPickup: () => Promise<void>;
  updateTextReminder: (textReminder: boolean) => void;
  cancelPickupClicked: () => void;
}

export const usePackagePickup = (
  navigation: Navigator,
  settings: CustomerPortalAppSettings | undefined,
  params: StackParamList[RouteName.PACKAGE_PICKUP],
  channel: Channel,
): UsePackagePickupResponse => {
  const returnContext = useContext(ReturnContext);
  const client = useContext(RpcClientContext);

  const today = useMemo(() => Temporal.Now.plainDateISO(), []);

  const needsManualReview =
    returnContext.draftReturn?.compensationMethods?.some(
      (method) => method.manualReview === true,
    ) ?? false;
  const minDays = needsManualReview ? MIN_DAYS_FOR_MANUAL_REVIEW : MIN_DAYS;
  const minDate = useMemo(() => getValidDate(today, minDays), [today, minDays]);

  const [pickupInfo, setPickupInfo] = useState<PickupData>({
    textReminder: false,
    pickupDate: minDate.toJSON(),
    pickupLocation: {
      packageLocation: PickupLocation.FRONT_DOOR,
      specialInstructions: null,
    },
    phone: "",
  });

  const updatePickupInfo = useCallback(
    (updates: Partial<PickupData>) => {
      setPickupInfo((prev) => ({
        ...prev,
        ...updates,
        pickupLocation: { ...prev.pickupLocation, ...updates.pickupLocation },
      }));
    },
    [setPickupInfo],
  );

  const updateTextReminder = useCallback(
    (reminder: boolean) => {
      updatePickupInfo({ textReminder: reminder });
    },
    [updatePickupInfo],
  );

  const datePickerState = useDatePickerState({
    today,
    minDays,
    minDate,
    updatePickupInfo,
  });
  const locationPickerState = useLocationPickerState(
    { pickupData: pickupInfo },
    updatePickupInfo,
  );

  const { submitPickup, isUpdatingMethod } = usePickupSubmission(
    client,
    returnContext,
    navigation,
    settings,
    pickupInfo,
    channel,
  );

  const formattedFee = useMemo(() => {
    return moneyFormat({
      amount: params.pickupFee,
      currency: returnContext.draftReturn?.currency || DEFAULT_CURRENCY,
    });
  }, [params.pickupFee, returnContext.draftReturn?.currency]);

  const selectedPickupLocation = useMemo(() => {
    return Object.keys(PickupLocation).find(
      (key) =>
        PickupLocation[key as keyof typeof PickupLocation] ===
        pickupInfo.pickupLocation.packageLocation,
    );
  }, [pickupInfo.pickupLocation.packageLocation]);

  const updatePickupInstructions = useCallback(
    (text: string) => {
      setPickupInfo({
        ...pickupInfo,
        pickupLocation: {
          ...pickupInfo.pickupLocation,
          specialInstructions: text,
        },
      });
    },
    [pickupInfo],
  );

  const validatePhone = useCallback((phone: string) => {
    const errors: string[] = [];
    if (!phone) {
      errors.push("Phone number is required");
    } else if (!isValidNonDefaultPhoneNumber(phone)) {
      errors.push("Please enter a valid phone number");
    }
    return errors;
  }, []);

  const updatePhone = useCallback(
    (phone: string) => {
      setPickupInfo({ ...pickupInfo, phone });
    },
    [pickupInfo],
  );

  return {
    datePickerState,
    draftReturn: returnContext.draftReturn,
    handleDateChange: datePickerState.handleDateChange,
    updatePickupInstructions,
    updatePhone,
    validatePhone,
    isUpdatingMethod,
    locationPickerState,
    texts: {
      addPickupButton: isUpdatingMethod ? "Loading..." : "Add pickup",
      cancelButton: "Cancel",
      description: `Your package will be picked up from the address on the next page. You will be charged an additional ${
        formattedFee
      }.`,
      header: "Schedule pickup",
      pickupDateLabel: "Pickup date",
      pickupLocationLabel: "Pickup location",
      pickupLocationTip:
        "If you live in a multi-story building, select an option which is ground level and secure.",
      selectLocationButton: "Select Location",
      specialInstructionsLabel: "Special instructions",
      textReminderDescription:
        "We will send you a text message when your package has been picked up by the carrier",
      textReminderLabel: "Notify me when my package is picked up",
      textReminderNotification: "Send me a text reminder the day before pickup",
      phoneLabel: "Phone number for pickup",
    },
    selectedPickupLocation,
    shippingDetails: {
      draftReturnId: returnContext.draftReturn?._id,
      pickupData: pickupInfo,
    },
    submitPickup,
    updateTextReminder,
    cancelPickupClicked: () => {
      datadogLogs.logger.info(
        `${PACKAGE_PICKUP_UPSELL_METRICS_LOG_STAMP} ${CANCEL_PICKUP_CLICKED_LOG_STAMP} ${returnContext.draftReturn?._id} customer clicked cancel in pickup details modal`,
      );
      navigation.pop();
    },
  };
};
